import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";

// ---------- ui helpers ----------
function cn(...c) {
  return c.filter(Boolean).join(" ");
}

function Button({ className, ...props }) {
  return (
    <button
      {...props}
      className={cn(
        "bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white font-semibold",
        "px-4 py-2 rounded-lg transition",
        className
      )}
    />
  );
}

function GhostButton({ className, ...props }) {
  return (
    <button
      {...props}
      className={cn(
        "bg-white hover:bg-gray-50 disabled:bg-gray-50 text-gray-900 font-semibold",
        "px-4 py-2 rounded-lg border border-gray-200 transition",
        className
      )}
    />
  );
}

function DangerButton({ className, ...props }) {
  return (
    <button
      {...props}
      className={cn(
        "bg-white hover:bg-red-50 disabled:bg-gray-50 text-red-700 font-semibold",
        "px-4 py-2 rounded-lg border border-red-200 transition",
        className
      )}
    />
  );
}

function Input({ className, ...props }) {
  return (
    <input
      {...props}
      className={cn(
        "w-full rounded-lg border border-gray-200 bg-white px-3 py-2",
        "focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-300",
        className
      )}
    />
  );
}

// ---------- pin helpers ----------
function normalizePin(value) {
  // keep digits only
  return String(value || "").replace(/\D/g, "");
}
function isValidPin(pin, length = 4) {
  return new RegExp(`^\\d{${length}}$`).test(String(pin || ""));
}
function generatePin(length = 4) {
  // 0000 -> 9999 style; preserve leading zeros
  const max = Math.pow(10, length);
  const n = Math.floor(Math.random() * max);
  return String(n).padStart(length, "0");
}

// ---------- main ----------
export default function PPMEngineersAdmin() {
  const PIN_LEN = 4;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const [engineers, setEngineers] = useState([]);
  const [q, setQ] = useState("");

  // editor state
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState("");
  const [initials, setInitials] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [active, setActive] = useState(true);

  // ✅ PIN
  const [pin, setPin] = useState("");
  const [showPins, setShowPins] = useState(false);

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const ref = collection(db, "ppmengineers");
      const snap = await getDocs(query(ref, orderBy("name")));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setEngineers(list);
    } catch (e) {
      setErr(e?.message || "Failed to load engineers.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return engineers;

    return engineers.filter((e) => {
      const hay = [
        e.name,
        e.initials,
        e.email,
        e.phone,
        e.notes,
        e.id,
        // deliberately NOT indexing pin for search (avoid leaking)
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(s);
    });
  }, [engineers, q]);

  function resetForm() {
    setEditingId(null);
    setName("");
    setInitials("");
    setEmail("");
    setPhone("");
    setNotes("");
    setActive(true);
    setPin("");
  }

  function startEdit(e) {
    setMsg("");
    setErr("");
    setEditingId(e.id);
    setName(e.name || "");
    setInitials(e.initials || "");
    setEmail(e.email || "");
    setPhone(e.phone || "");
    setNotes(e.notes || "");
    setActive(e.active !== false);
    setPin(String(e.pin || "")); // ✅ load existing pin
  }

  async function save() {
    setErr("");
    setMsg("");

    const n = name.trim();
    if (!n) {
      setErr("Name is required.");
      return;
    }

    const p = normalizePin(pin);
    if (!isValidPin(p, PIN_LEN)) {
      setErr(`PIN is required and must be exactly ${PIN_LEN} digits.`);
      return;
    }

    const payload = {
      name: n,
      initials: initials.trim() || null,
      email: email.trim() || null,
      phone: phone.trim() || null,
      notes: notes.trim() || null,
      active: Boolean(active),

      // ✅ always store pin as string
      pin: p,

      updatedAt: serverTimestamp(),
    };

    setSaving(true);
    try {
      if (editingId) {
        await updateDoc(doc(db, "ppmengineers", editingId), payload);
        setEngineers((prev) =>
          prev.map((x) => (x.id === editingId ? { ...x, ...payload } : x))
        );
        setMsg("Engineer updated.");
      } else {
        const ref = await addDoc(collection(db, "ppmengineers"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        setEngineers((prev) =>
          [...prev, { id: ref.id, ...payload, createdAt: new Date() }].sort((a, b) =>
            String(a.name).localeCompare(String(b.name))
          )
        );
        setMsg("Engineer added.");
      }
      resetForm();
    } catch (e) {
      setErr(e?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(e) {
    setErr("");
    setMsg("");
    setSaving(true);
    try {
      const nextActive = !(e.active !== false);
      await updateDoc(doc(db, "ppmengineers", e.id), {
        active: nextActive,
        updatedAt: serverTimestamp(),
      });
      setEngineers((prev) =>
        prev.map((x) => (x.id === e.id ? { ...x, active: nextActive } : x))
      );
    } catch (ex) {
      setErr(ex?.message || "Failed to update active state.");
    } finally {
      setSaving(false);
    }
  }

  async function removeEngineer(e) {
    const ok = window.confirm(
      `Delete engineer "${e.name}"?\n\nThis does NOT update existing jobs. It's usually better to set inactive instead.`
    );
    if (!ok) return;

    setErr("");
    setMsg("");
    setSaving(true);
    try {
      await deleteDoc(doc(db, "ppmengineers", e.id));
      setEngineers((prev) => prev.filter((x) => x.id !== e.id));
      if (editingId === e.id) resetForm();
      setMsg("Engineer deleted.");
    } catch (ex) {
      setErr(ex?.message || "Delete failed.");
    } finally {
      setSaving(false);
    }
  }

  function applyRandomPin() {
    setPin(generatePin(PIN_LEN));
  }

  return (
    <div className="p-6 max-w-6xl space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900">PPM Engineers</h1>
          <p className="text-gray-600 mt-1">
            Manage engineers used for scheduling assignments (includes a simple PIN for kiosk login).
          </p>
        </div>
        <div className="flex gap-2">
          <GhostButton onClick={load} disabled={loading || saving}>
            Refresh
          </GhostButton>
          <GhostButton onClick={resetForm} disabled={saving}>
            New
          </GhostButton>
        </div>
      </div>

      {err ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-800">
          <div className="font-bold">Problem</div>
          <div className="mt-1">{err}</div>
        </div>
      ) : null}

      {msg ? (
        <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-green-800">
          <div className="font-bold">OK</div>
          <div className="mt-1">{msg}</div>
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* list */}
        <div className="lg:col-span-2 rounded-2xl border border-gray-200 bg-white overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-lg font-extrabold text-gray-900">Engineers</div>
                <div className="text-sm text-gray-600">{filtered.length} shown</div>
              </div>

              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                  <input
                    type="checkbox"
                    checked={showPins}
                    onChange={(e) => setShowPins(e.target.checked)}
                    className="h-4 w-4 accent-purple-600"
                  />
                  Show PINs
                </label>

                <div className="w-[260px]">
                  <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search…"
                  />
                </div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="p-6 text-gray-600">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-gray-600">No engineers found.</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filtered.map((e) => {
                const isInactive = e.active === false;
                const hasPin = Boolean(String(e.pin || "").trim());
                return (
                  <div
                    key={e.id}
                    className={cn(
                      "p-4 flex items-start justify-between gap-3",
                      isInactive && "opacity-70"
                    )}
                  >
                    <div>
                      <div className="font-extrabold text-gray-900">
                        {e.name || "Unnamed"}
                        {isInactive ? (
                          <span className="ml-2 text-xs font-semibold text-red-700">INACTIVE</span>
                        ) : null}
                      </div>

                      <div className="text-xs text-gray-500 mt-1">
                        {e.initials ? `(${e.initials})` : null}
                        {e.email ? ` · ${e.email}` : ""}
                        {e.phone ? ` · ${e.phone}` : ""}
                      </div>

                      <div className="text-xs text-gray-500 mt-1">
                        PIN:{" "}
                        {hasPin ? (
                          showPins ? (
                            <span className="font-mono font-semibold text-gray-900">{String(e.pin)}</span>
                          ) : (
                            <span className="font-semibold text-gray-700">set</span>
                          )
                        ) : (
                          <span className="font-semibold text-red-700">MISSING</span>
                        )}
                      </div>

                      {e.notes ? <div className="text-xs text-gray-500 mt-1">{e.notes}</div> : null}
                    </div>

                    <div className="flex gap-2">
                      <GhostButton onClick={() => startEdit(e)} disabled={saving}>
                        Edit
                      </GhostButton>
                      <GhostButton onClick={() => toggleActive(e)} disabled={saving}>
                        {e.active === false ? "Activate" : "Deactivate"}
                      </GhostButton>
                      <DangerButton onClick={() => removeEngineer(e)} disabled={saving}>
                        Delete
                      </DangerButton>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* editor */}
        <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <div className="text-lg font-extrabold text-gray-900">
              {editingId ? "Edit engineer" : "Add engineer"}
            </div>
            <div className="text-sm text-gray-600">
              Name + PIN are required for kiosk login.
            </div>
          </div>

          <div className="p-4 space-y-3">
            <div>
              <div className="text-sm font-semibold text-gray-800">
                Name <span className="text-red-500">*</span>
              </div>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-sm font-semibold text-gray-800">Initials</div>
                <Input
                  value={initials}
                  onChange={(e) => setInitials(e.target.value)}
                  placeholder="e.g. BJ"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                  <input
                    type="checkbox"
                    checked={active}
                    onChange={(e) => setActive(e.target.checked)}
                    className="h-4 w-4 accent-purple-600"
                  />
                  Active
                </label>
              </div>
            </div>

            {/* ✅ PIN */}
            <div>
              <div className="text-sm font-semibold text-gray-800">
                PIN <span className="text-red-500">*</span>
              </div>
              <div className="flex gap-2">
                <Input
                  value={pin}
                  onChange={(e) => setPin(normalizePin(e.target.value))}
                  placeholder={`Exactly ${PIN_LEN} digits`}
                  inputMode="numeric"
                  maxLength={PIN_LEN}
                />
                <GhostButton type="button" onClick={applyRandomPin} disabled={saving}>
                  Random
                </GhostButton>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Must be exactly {PIN_LEN} digits. Not secure — just identity selection.
              </div>
            </div>

            <div>
              <div className="text-sm font-semibold text-gray-800">Email</div>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>

            <div>
              <div className="text-sm font-semibold text-gray-800">Phone</div>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>

            <div>
              <div className="text-sm font-semibold text-gray-800">Notes</div>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>

            <div className="flex gap-2 pt-2">
              <Button onClick={save} disabled={saving}>
                {saving ? "Saving…" : editingId ? "Save changes" : "Add engineer"}
              </Button>
              <GhostButton onClick={resetForm} disabled={saving}>
                Clear
              </GhostButton>
            </div>

            {editingId ? (
              <div className="text-xs text-gray-500 pt-2">
                Doc ID: <span className="font-mono">{editingId}</span>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
